import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { EdgePatientEnrollmentApi, EdgeEnrollmentApi, EdgePayloadStagingApi, EscoordV1Api } from '@trialcard/apigateway.client';
import {
    EnrollPatientRequestModel,
    EnterpriseCoordinatorAccountUpsearchRequestModel,
    UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
    EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    EnterpriseCoordinatorSharedAddressUpsearchModel,
    EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
    EnterpriseCoordinatorSharedPhoneUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel,
    UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountExternalIdModel,
    EnterpriseCoordinatorSiteServiceModelsSitePointOfContactUpsearchModel,
    EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    StagePayloadRequest,
} from '@trialcard/apigateway.models';
import { ContactInfoType, Gender, RoleType,ContactInfoPurposeType } from '@trialcard/enums';
import moment, { Moment } from 'moment';
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { PDFDocument } from 'pdf-lib';
import DocumentUploadModule from './documentUpload';
import EligibilityModule from './eligibility';
import LayoutModule from './layout';
import { Settings } from '~/services/settings';
import SurveyModule from '~/store/survey';
import { rolling365 } from '~/tests/e2e/@data/shared/dates';
import { pharmacyMN, medicalMN } from '~/tests/e2e/@data/shared/memberNumbers';
import genders from '~/assets/genders';

@InjectModule({ stateFactory: true }, module)
export default class EnrollmentModule extends InjectVuexModule {
    @Resolve
    public uploadModule!: DocumentUploadModule;

    @Resolve
    public eligibilityData!: EligibilityModule;

    @Resolve
    public patientEnrollmentApi!: EdgePatientEnrollmentApi;

    @Resolve
    public enrollmentApi!: EdgeEnrollmentApi;

    @Resolve
    public escoordV1Api!: EscoordV1Api;

    @Resolve
    public settings!: Settings;

    @Resolve
    public survey!: SurveyModule;

    @Resolve
    public payloadStagingApi!: EdgePayloadStagingApi;

    @Resolve
    public layout!: LayoutModule;

    alternativeEnrollment: boolean | null = null;
    enrollmentTypePatientOrHCP = '';

    defaultPatientInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: null as Moment | null,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        gender: null as Gender | null,
    };

    defaultPatientInformationOtherFieldData = {
        emailCheckbox: false as boolean,
        phoneCheckbox: false as boolean,
        preferNotToSay: false as boolean,
        onDemand: false as boolean,
        prophy: false as boolean,
        radioQuestion: '' as string,
    };

    defaultPrescriberData = [
        {
            model: {
                firstName: '',
                lastName: '',
                npi: '',
                accountRelationshipIsPrimary: true,
                prescriberLicenses: [],
                externalIds: [],
                addresses: [],
                phoneNumbers: [],
                emailAddresses: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[];

    defaultSiteData = [
        {
            model: {
                accountRelationshipIsPrimary: true,
                siteRoleType: RoleType.PrescriberSite,
                pointOfContacts: [] as EnterpriseCoordinatorSiteServiceModelsSitePointOfContactUpsearchModel[],
                description: '',
                addresses: [
                    {
                        addressOne: '',
                        addressTwo: '',
                        zip: '',
                        city: '',
                        state: '',
                    },
                ],
                contacts: [],
                emailAddresses: [],
                externalIds: [],
                phoneNumbers: [],
                globalIds: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[];

    defaultContactPreferences = [] as EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[];

    defaultMemberNumbers = {
        medical: medicalMN.number as string,
        pharmacy: pharmacyMN.number as string,
    };
    currentDrugNamePath = '';

    defaultExpirationDate = rolling365;
    defaultExternalIds = [] as EnterpriseCoordinatorAccountServiceModelsAccountExternalIdModel[];

    pharmaMemberNumber = this.defaultMemberNumbers.pharmacy;
    medicalMemberNumber = this.defaultMemberNumbers.medical;

    expirationDate = this.defaultExpirationDate;
    patientInformationData = { ...this.defaultPatientInformationData };
    prescriberData = [...this.defaultPrescriberData];
    siteData = [...this.defaultSiteData];
    externalIds = [...this.defaultExternalIds];
    patientInformationOtherField = { ...this.defaultPatientInformationOtherFieldData };
    contactPreferences = [...this.defaultContactPreferences];
    stagePayloadSuccess = false;
    copayProgramType = '';

    @Mutation
    public async updatePatientInformation(newPatientInformation: typeof EnrollmentModule.prototype.patientInformationData) {
        this.patientInformationData = { ...newPatientInformation };
    }

    @Mutation
    public async updatePatientInformationEmailContactInfo(emailAddresses:EnterpriseCoordinatorSharedEmailAddressUpsearchModel[]) {
        this.patientInformationData.emailAddresses = [ ...emailAddresses ];
    }

    @Mutation
    public async updatePatientInformationPhoneContactInfo(phoneNumbers:EnterpriseCoordinatorSharedPhoneUpsearchModel[]) {
        this.patientInformationData.phoneNumbers = [...phoneNumbers ];
    }

    @Mutation
    public async updatePrescribers(newPrescriberData: UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[]) {
        this.prescriberData = [...newPrescriberData];
    }

    @Mutation
    public async updateSites(newSites: UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[]) {
        this.siteData = [...newSites];
    }

    @Mutation
    public async updateExternalIds(externalId: EnterpriseCoordinatorAccountServiceModelsAccountExternalIdModel[]) {
        this.externalIds = [...externalId];
    }

    @Mutation
    public async updateContactPreferences(newContactPreferences: EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[]) {
        this.contactPreferences = [...newContactPreferences];
    }

    @Mutation
    public setEnrollmentType(alternative: boolean) {
        this.alternativeEnrollment = alternative;
    }

    @Mutation
    public setEnrollmentTypePatientOrHCP(alternative: string) {
        this.enrollmentTypePatientOrHCP = alternative;
        localStorage.setItem('enrollmentTypePatientOrHCP', alternative);
    }

    @Mutation
    public setDrugNamePath(value: string) {
        this.currentDrugNamePath = value;
        localStorage.setItem('drugNamePath', value);
    }

    @Mutation
    public setPharmaMemberNumber(pharmaMemberNumber: string) {
        this.pharmaMemberNumber = pharmaMemberNumber;
    }

    @Mutation
    public setMedicalMemberNumber(medicalMemberNumber: string) {
        this.medicalMemberNumber = medicalMemberNumber;
    }

    @Mutation
    public setExpirationDate(expirationDate: Moment) {
        this.expirationDate = expirationDate;
    }

    @Mutation
    public setPatientInformationOtherField(newPatientInformationOther: typeof EnrollmentModule.prototype.patientInformationOtherField) {
        this.patientInformationOtherField = { ...newPatientInformationOther };
    }

    @Mutation
    public setStagePayloadSuccess(stagePayloadSuccess: boolean) {
        this.stagePayloadSuccess = stagePayloadSuccess;
    }

    public checkBaseOrAccumulator = null
    @Mutation
    public setBaseOrAccumulatorSuccess(baseOrAccu: any) {
        this.checkBaseOrAccumulator = baseOrAccu;
    }

    @Mutation
    public setCopayProgramType(copayProgramType: 'Base' | 'Accumulator' | 'Maximizer') {
        this.copayProgramType = copayProgramType;
    }

    @Action({ rawError: true })
    public clearStore() {
        this.eligibilityData.updateEligibilityData([]);
        this.setPharmaMemberNumber(this.defaultMemberNumbers.pharmacy);
        this.setMedicalMemberNumber(this.defaultMemberNumbers.medical);
        this.setExpirationDate(this.defaultExpirationDate);
        this.updatePatientInformation(this.defaultPatientInformationData);
        this.updatePrescribers(this.defaultPrescriberData);
        this.updateSites(this.defaultSiteData);
        this.uploadModule.clearFiles();
        this.setPatientInformationOtherField(this.defaultPatientInformationOtherFieldData);
        localStorage.removeItem('drugNamePath');
        this.updateContactPreferences(this.defaultContactPreferences);
    }

    @Action({ rawError: true })
    public clearEnrollment() {
        this.updatePatientInformation({ ...this.defaultPatientInformationData });
        this.setPharmaMemberNumber(this.defaultMemberNumbers.pharmacy);
        this.setMedicalMemberNumber(this.defaultMemberNumbers.medical);
    }

    @Action({ rawError: true })
    public async submitToAPI() {
        let response = null;
        const { prescriberData, siteData, externalIds, copayProgramType } = this;
        try {
            const patient = {
                account: {
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            ...this.patientInformationData,
                            middleName: '',
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            accountType: 1,
                            accountStatus: 1,
                            externalIds,
                        },
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    prescriberData,
                    siteData,
                    surveySessionData: [this.survey.sessionId] as number[],
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
                // accountAttestations: [
                //     {
                //         code: 'eServiceAttestion',
                //         startDate: moment(),
                //     },
                // ],
                copayProgramType
            };
            const axios: AxiosInstance = (this.patientEnrollmentApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: '/edge/patientEnrollment/enrollPatient',
                method: 'POST',
                data: patient,
                headers: {
                    'x-program-id': this.settings.programId!,
                },
            };
            await Promise.all([(this.patientEnrollmentApi as any).authentications.Token.applyToRequest(axiosRequest)]);
            response = await axios.request(axiosRequest);
            // response = await this.patientEnrollmentApi.patientEnrollmentEnrollPatient(patient, this.settings.programId);
        } catch {
            response = { status: 400 } as AxiosResponse;
        }
        return response;
    }

    @Action({ rawError: true })
    public async submitToAccountUpsearch() {
        let response = null;
        const { prescriberData, siteData, externalIds } = this;
        try {
            const accountUpsearch = {
                patientData: {
                    canUpdate: true,
                    canInsert: true,
                    skipSearch: false,
                    model: {
                        ...this.patientInformationData,
                        middleName: '',
                        suffix: '',
                        nickname: '',
                    } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                accountData: {
                    canUpdate: true,
                    canInsert: true,
                    skipSearch: false,
                    model: {
                        accountType: 1,
                        accountStatus: 1,
                        externalIds,
                        contactPreferences: this.contactPreferences,
                    },
                } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                prescriberData,
                siteData,
                surveySessionData: [this.survey.sessionId] as number[],
            } as EnterpriseCoordinatorAccountUpsearchRequestModel;
            response = await this.escoordV1Api.accountUpsearchByTenantIdProgramId(
                accountUpsearch,
                (this.settings.programId as unknown) as number
            );
        } catch {
            response = { status: 400 } as AxiosResponse;
        }
        return response;
    }

    async storeData(document: string) {
        try {
            const request = {
                metaData: {},
                contentType: 'application/json',
                data: { document },
                systemCode: 'Portico',
                systemId: this.settings.programId,
                purposeCode: 'SanofiWAYRILZ',
                purposeId: `${this.pharmaMemberNumber}`,
            };
            await this.asyncStageDocument(3, request);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error from storing data:', error);
        }
    }

    async asyncStageDocument(retryCounter: number, stagePayloadRequest: StagePayloadRequest): Promise<StagePayloadRequest> {
        await this.setStagePayloadSuccess(false);
        return this.payloadStagingApi.payloadStagePayload(stagePayloadRequest).then(async (data) => {
            if (data && data.data && data.data.success) {
                await this.setStagePayloadSuccess(true);
                return stagePayloadRequest;
            }
            if (retryCounter > 0 && !this.stagePayloadSuccess) {
                retryCounter--;
                await new Promise((resolve) => setTimeout(resolve, 1000));
                return this.asyncStageDocument(retryCounter, stagePayloadRequest);
            }
            return stagePayloadRequest;
        }).catch(async (error) => {
            console.error('error from staging payload', error);
            if (retryCounter > 0) {
                retryCounter--;
                await new Promise((resolve) => setTimeout(resolve, 1000));
                return this.asyncStageDocument(retryCounter, stagePayloadRequest);
            }
            return stagePayloadRequest;
        });
    }

    async writePdf() {
        const isHcp = localStorage.getItem('enrollmentTypePatientOrHCP') === 'HCP';
        const url = isHcp ? '/enrollment_form_hcp.pdf' : '/enrollment_form_patient.pdf'
        const pdfArrayRes = await fetch(this.settings.url(url));
        const formBytes = await pdfArrayRes.arrayBuffer();
        const baseDoc = await PDFDocument.load(formBytes);
        const prescriber =
            (this.prescriberData && this.prescriberData.length > 0 && this.prescriberData[0].model) ||
            ({} as EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel);
        const site =
            (this.siteData && this.siteData.length > 0 && this.siteData[0].model) ||
            ({} as EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel);
        const form = baseDoc.getForm();
        const gender = genders.find(gender => gender.id === this.patientInformationData.gender);
        const address = this.patientInformationData.addresses.length > 0 ? this.patientInformationData.addresses[0] : {};
        const patientPhone = this.patientInformationData.phoneNumbers.length > 0 ? this.patientInformationData.phoneNumbers[0] : {};
        const patientEmail = this.patientInformationData.emailAddresses.length > 0 ? this.patientInformationData.emailAddresses[0] : {};
        const prescriberPhone =
            prescriber && prescriber.phoneNumbers && prescriber.phoneNumbers.length > 0 ? prescriber.phoneNumbers[0] : {};
        const siteContacts = site && site.pointOfContacts && site.pointOfContacts.length > 0 ? site.pointOfContacts[0] : {};
        const siteAddress = site && site.addresses && site.addresses.length > 0 ? site.addresses[0] : {};
        const dateOfBirth = this.patientInformationData.dateOfBirth ? moment.utc(this.patientInformationData.dateOfBirth || '', ['MM/DD/YYYY', 'YYYY-MM-DD']) : moment();
        form.getTextField('userType').setText('HCP');
        form.getTextField('drugSelected').setText('WAYRILZ');
        form.getTextField('patientFirstName').setText(this.patientInformationData.firstName);
        form.getTextField('patientLastName').setText(this.patientInformationData.lastName);
        form.getTextField('patientGender').setText((gender && gender.name) || '');
        form.getTextField('dateOfBirth').setText(dateOfBirth.format('L'));
        form.getTextField('patientAddressLineOne').setText(address.addressOne);
        form.getTextField('patientAddressLineTwo').setText(address.addressTwo);
        form.getTextField('patientZip').setText(address.zip);
        form.getTextField('patientCity').setText(address.city);
        form.getTextField('patientState').setText(address.state);
        form.getTextField('patientPhone').setText(patientPhone.phoneNumber);
        form.getTextField('patientEmail').setText(patientEmail.address);
        form.getTextField('prescriberFirstName').setText((prescriber && prescriber.firstName) || '');
        form.getTextField('prescriberLastName').setText((prescriber && prescriber.lastName) || '');
        form.getTextField('prescriberPhone').setText(prescriberPhone.phoneNumber);
        form.getTextField('siteName').setText((site && site.description) || '');
        form.getTextField('siteContactName').setText(siteContacts.firstName);
        form.getTextField('siteContactEmail').setText(siteContacts.emailAddress);
        form.getTextField('siteContactPhone').setText(siteContacts.phoneNumber);
        form.getTextField('siteFax').setText(siteContacts.faxNumber);
        form.getTextField('siteAddressLineOne').setText(siteAddress.addressOne);
        form.getTextField('siteAddressLineTwo').setText(siteAddress.addressTwo);
        form.getTextField('siteZip').setText(siteAddress.zip);
        form.getTextField('siteCity').setText(siteAddress.city);
        form.getTextField('siteState').setText(siteAddress.state);
        if (this.patientInformationOtherField.emailCheckbox) {
            form.getCheckBox('contactEmail').check();
        }
        if (this.patientInformationOtherField.phoneCheckbox) {
            form.getCheckBox('contactPhone').check();
        }
        if (this.patientInformationOtherField.prophy) {
            form.getCheckBox('treatProphy').check();
        }
        if (this.patientInformationOtherField.onDemand) {
            form.getCheckBox('treatOnDemand').check();
        }
        if (this.patientInformationOtherField.preferNotToSay) {
            form.getCheckBox('treatNA').check();
        }
        const learnMoreAnswers = [
            this.settings.hcpInterestedAlprolix,
            this.settings.hcpInterestedEloctate,
            this.settings.hcpInterestedAltuviiio,
            this.settings.patientInterestedAlprolix,
            this.settings.patientInterestedEloctate,
            this.settings.patientInterestedAltuviiio,
        ];
        const isPrescribedAnswers = [
            this.settings.hcpChildAlprolix,
            this.settings.hcpChildEloctate,
            this.settings.hcpChildAltuviiio,
            this.settings.patientChildAlprolix,
            this.settings.patientChildEloctate,
            this.settings.patientChildAltuviiio,
        ];
        const found =
            this.eligibilityData.eligibilityData.find(question => question.questionId === +this.settings.marketingOptInQuestionIdPatient) ||
            {};
        let marketingAuthAnswer = 'no';
        if (found.answerId === +this.settings.marketingOptInAnswerYesIdPatient || found.answerId === +this.settings.marketingOptInAnswerYesIdHcp) {
            marketingAuthAnswer = 'yes';
            const answer = learnMoreAnswers.includes(this.patientInformationOtherField.radioQuestion)
                ? 'learnMore'
                : isPrescribedAnswers.includes(this.patientInformationOtherField.radioQuestion)
                ? 'isPrescribed'
                : '';
            if (answer.trim()) {
                form.getRadioGroup('group1').select(answer);
            }
        }

        form.getRadioGroup('marketingAuthorization').select(marketingAuthAnswer);
        // this corresponds to the consent checkbox on the eligibility page for both patient and hcp.
        // user cannot get to this point without checking this box, so this will be checked by default.
        form.getCheckBox('consentToTC').check();
        const base64Document = await baseDoc.saveAsBase64();

        // Allows viewing the document for testing purposes

// if (this.layout.testPDF) {
//             const byteArray = await baseDoc.save();
//             const blob = new Blob([byteArray], { type: 'application/pdf' });
//             const link = document.createElement('a');
//             link.href = window.URL.createObjectURL(blob);
//             link.target = '_blank';
//             link.click();
//         }

// return base64Document;
        if (this.layout.testPDF) {
            const byteArray = await baseDoc.save();
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            console.log(link);
            link.target = '_blank';
            link.click();
        }
        return base64Document;
    }
}
